import { isEmpty } from 'lodash';
import { FilterValue } from 'antd/lib/table/interface';

import { getTimeRangeFromXMonthsAgo } from 'components/organisms/Lists/ListsHelper';
import { HeaderSearch, SearchByField } from 'shared/interfaces';
import { Filters } from 'shared/types';
import { ListingSearchState } from 'store/listingsSearchParams/types';
import { store } from 'store';
import apiListing from 'utils/http/apiListing';
import { ListingStatusId, ListingWorkflowStatusId } from 'utils/const';

const { Created, Live, Sold, Hidden, Removed } = ListingStatusId;

export const searchBy = {
  available: (filterQuery?: string[]) => {
    let query = `(or listing_status_id:${Created} listing_status_id:${Live} listing_status_id:${Hidden})`;

    if (filterQuery) {
      query = `( and ${filterQuery.join(' ')} ${query} )`;
    }

    return query;
  },
  pendingBreeder: (filterQuery?: string[]) => {
    const { Postponed } = ListingWorkflowStatusId;
    let query = `(and workflow_status_id:${Postponed} (not (or listing_status_id:${Removed} listing_status_id:${Sold})))`;
    if (filterQuery) {
      query = `( and ${filterQuery.join(' ')} ${query} )`;
    }

    return query;
  },
  unavailable: (filterQuery?: string[]) => {
    const monthsInThePast = 3;
    const rangeDateFrom = getTimeRangeFromXMonthsAgo(monthsInThePast);
    let query = `(and logical_updated_at:[${rangeDateFrom}] (or listing_status_id:${Removed} listing_status_id:${Sold}))`;

    if (filterQuery) {
      query = `( and ${filterQuery.join(' ')} ${query} )`;
    }

    return query;
  },
  listingsIds: (ids: number[], searchByField?: SearchByField) => {
    if (isEmpty(ids)) {
      return '';
    }

    const idList = ids.map((id) => ` listing_id:${id}`).join(' ');
    return `(or ${idList})`;
  },
  search: (filterQuery?: string[], headerSearch?: HeaderSearch) => {
    let value = store.getState().listingsSearchParams.search?.value;
    let filter = store.getState().listingsSearchParams.search?.filter;

    if (headerSearch) {
      value = headerSearch.value;
      filter = headerSearch.filter;
    }

    return `${filter}:${value}`;
  },
};

export const getSearchTypeAndQuery = (
  filterBy: string,
  data: number[],
  searchByField?: SearchByField,
  headerSearch?: HeaderSearch
) => {
  let query;

  switch (filterBy) {
    case 'available':
      query = searchBy.available(searchByField?.filterQuery);
      break;
    case 'pendingBreeder':
      query = searchBy.pendingBreeder(searchByField?.filterQuery);
      break;
    case 'unavailable':
      query = searchBy.unavailable(searchByField?.filterQuery);
      break;
    case 'test':
      query = searchBy.available(searchByField?.filterQuery);
      break;
    case 'search':
      query = searchBy.search(searchByField?.filterQuery, headerSearch);
      break;
    case 'queue':
      query = searchBy.listingsIds(data, searchByField);
      break;
    default:
      query = searchBy.available();
  }

  return query;
};

export const setEditTypeValue = (type: string) => {
  let value;
  switch (type) {
    case 'new_listings':
      value = 'new';
      break;
    default:
      value = type;
  }
  return value;
};

export const buildFilterQuery = (filters: Filters): string[] => {
  return Object.keys(filters).map((filterKey) => {
    const partialFilterQuery = filters[filterKey]
      ?.reduce((acc: string[], curr: number | string | boolean) => {
        const key = ` ${filterKey}: '${curr}'`;
        return acc.concat(key);
      }, [])
      .join(' ');

    return Boolean(partialFilterQuery) ? `(or ${partialFilterQuery}) ` : '';
  });
};

export const buildFilterParams = (filter: string[], filterName: string) => {
  return filter
    ?.reduce((acc: string[], curr: string, index: number) => {
      const key = `${filterName}[${index}]=${curr}`;
      return acc.concat(key);
    }, [])
    .join('&');
};

export const getQueue = async (
  listingsSearchParams: ListingSearchState,
  editType: string
) => {
  const { filter, sort, page, size } = listingsSearchParams;

  let listingStatusId = '';
  let inventoryStatusId = '';
  let lockedByUser = '';
  let breederName = '';
  let breed = '';
  if (filter !== null) {
    listingStatusId = buildFilterParams(
      filter?.listing_status_id as string[],
      'listing_status_id'
    );

    inventoryStatusId = buildFilterParams(
      filter?.inventory_status_id as string[],
      'inventory_status_id'
    );

    lockedByUser = getParamFromArray(filter?.locked_by_user_name, 0);
    breederName = getParamFromArray(filter?.breeder, 0);
    breed = getParamFromArray(filter?.breed_ad_label, 0);
  }

  let sortDirection = '';
  let sortBy = '';
  if (sort?.order) {
    sortDirection = sort.order === 'descend' ? 'desc' : 'asc';
    sortBy = String(sort.columnKey);
  }

  return await apiListing.queue({
    page,
    pageSize: size,
    editType,
    listingStatusId,
    sortDirection,
    sortBy,
    inventoryStatusId,
    lockedByUser,
    breederName,
    breed,
  });
};

/**
 * Get the a param as string from array, also check whether array is null, empty or undefined in any of those cases it will return an empty string.
 * @param arr The Array where the value will be taken from.
 * @param index The index (number) of the desired value in the array.
 * @returns value as string in case it exists in the array, otherwise it returns an empty string
 */
const getParamFromArray = (arr: FilterValue | null, index: number): string =>
  (arr?.[index] ?? '') as string;
